import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
/**
 * Audit trail — read-only contracts over onshelf.audit_log (populated by the
 * generic capture trigger). Tenant-scoped, admin-only on the API side.
 */
export const auditAction = z.enum(["INSERT", "UPDATE", "DELETE"]);
/**
 * Human label per audited table — the "service" the change belongs to. The UI
 * filter + the table's Service column use this; the raw `entityTable` stays the
 * filter value. Unknown tables fall back to a title-cased table name (service).
 */
export const AUDIT_ENTITY_LABELS = {
    stores: "Stores",
    store_users: "Store access",
    store_config: "Store config",
    store_categories: "Store categories",
    tenant_config: "Company config",
    tenant_users: "Members",
    user_invitations: "Invitations",
    categories: "Categories",
    products: "Products",
    product_barcodes: "Product barcodes",
    product_images: "Product images",
    product_variant_candidates: "Variant candidates",
    zones: "Zones",
    fixtures: "Fixtures",
    shelves: "Shelves",
    capture_devices: "Capture devices",
    capture_sessions: "Capture sessions",
    capture_tasks: "Capture tasks",
    store_product_prices: "Pricing",
    promotions: "Promotions",
    promotion_products: "Promotion products",
    promotion_store_assignments: "Promotion stores",
    planograms: "Planograms",
    planogram_assignments: "Planogram assignments",
    planogram_positions: "Planogram positions",
    issues: "Issues",
    store_tasks: "Tasks",
    webhooks: "Webhooks",
};
const auditActor = z.object({
    id: z.string().uuid(),
    email: z.string(),
    name: z.string().nullable(),
});
/** Lightweight list row (no full before/after — fetched in the detail view). */
export const auditLogInfo = z.object({
    id: z.string().uuid(), // public_id
    action: auditAction,
    entityTable: z.string(),
    entityLabel: z.string(),
    entityId: z.string().uuid().nullable(),
    actor: auditActor.nullable(),
    source: z.string(),
    changedFields: z.array(z.string()),
    createdAt: z.string().datetime(),
});
/** Full detail — includes the before/after snapshots + field-level diff. */
export const auditLogDetail = auditLogInfo.extend({
    requestId: z.string().nullable(),
    before: z.record(z.unknown()).nullable(),
    after: z.record(z.unknown()).nullable(),
    diff: z.record(z.object({ before: z.unknown(), after: z.unknown() })).nullable(),
});
export const listAuditLogsQuery = paginationQuerySchema.extend({
    entityTable: z.string().optional(),
    action: auditAction.optional(),
    actorId: z.string().uuid().optional(),
    entityId: z.string().uuid().optional(),
    source: z.string().optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    search: z.string().max(255).optional(),
});
export const auditLogsListResponse = pagedResponse(auditLogInfo);
/** Distinct values present in this tenant's log, for the filter dropdowns. */
export const auditFacetsResponse = z.object({
    entityTables: z.array(z.object({ table: z.string(), label: z.string() })),
    actions: z.array(auditAction),
    actors: z.array(auditActor),
    sources: z.array(z.string()),
});
/** Same filters as list, minus pagination — drives the CSV export. */
export const exportAuditLogsQuery = listAuditLogsQuery.omit({ page: true, pageSize: true });
//# sourceMappingURL=audit.schemas.js.map